import { Plane } from '@/modules/Editor/utils/raster/Plane'
import type { Bounds } from '@/modules/Editor/models/raster/Raster'

// Max scanning size for stability. After 4 Mlns circa the execution starts to get extremely slow,
// so we need to limit and stop execution when stack size is too big
const MAX_STACK_SIZE = 4_000_000

export type DFSFillingResult = {
  found: {
    [index: number]: [number, number][]
  }
  bounds: Bounds
  depth: { minZ: number; maxZ: number }
  error: DSFError | null
}

type DFSFillingPoint = [number, number, number]
type DFSFillingStack = DFSFillingPoint[]

export const DSFError = {
  UNKNOWN: 'UNKNOWN',
  STACK_SIZE_EXCEEDED: 'STACK_SIZE_EXCEEDED',
} as const

export type DSFError = (typeof DSFError)[keyof typeof DSFError]

export type DFSParams = {
  x: number
  y: number
  z: number
  w: number
  h: number
  buffer: Int16Array
  totalFrames: number
  initialBounds: Bounds
  plane: Plane
  maxVoxelValue: number
  minVoxelValue: number
  navigateZ: boolean
}

/**
 * Since this code is meant to run inside a worker, we are duplicating this function from the voxel raster
 */
const getVoxelValueAt = (
  dataBuffer: Int16Array,
  x: number,
  y: number,
  z: number,
  w: number,
  h: number,
  plane: Plane | undefined,
): number => {
  const size = w * h

  if (plane === Plane.Y) {
    return dataBuffer[y * size + z * w + x]
  }

  if (plane === Plane.X) {
    return dataBuffer[y * size + x * w + z]
  }

  // Default Plane.Z
  return dataBuffer[z * size + y * w + x]
}

/**
 * Function to use Depth-first search to scan a 3D volume.
 * Given the tolerance and starting from the initial starting point, it will scan all neighbours and
 * return those that are within the tolerance range, if connected to at least another point in the
 * range, together with the 3D bounds and total depth of exploration.
 *
 * @param navigateZ If true, the algorithm will also scan the Z axis, otherwise it will perform a 2D scan
 */
export const runDFSScan = (params: DFSParams): DFSFillingResult => {
  const {
    x,
    y,
    z,
    w,
    h,
    buffer,
    totalFrames,
    initialBounds,
    plane,
    maxVoxelValue,
    minVoxelValue,
    navigateZ,
  } = params
  const visitedVoxels = Object.create(null)
  const startPoint: DFSFillingPoint = [x, y, z]
  const stack: DFSFillingStack = [startPoint]

  const dfsBounds: Bounds = initialBounds
  const dfsZRange = { minZ: z, maxZ: z }
  const found: DFSFillingResult['found'] = Object.create(null)
  let error: DFSFillingResult['error'] = null

  while (stack.length > 0) {
    const [curX, curY, curZ] = stack.pop() as [number, number, number]
    visitedVoxels[`${curX},${curY},${curZ}`] = true

    if (curX < 0 || curX >= w || curY < 0 || curY >= h) {
      continue
    }
    if (curZ && (curZ < 0 + 1 || curZ >= totalFrames)) {
      continue
    }

    const voxelValue = getVoxelValueAt(buffer, curX, curY, curZ, w, h, plane)
    if (voxelValue > maxVoxelValue || voxelValue < minVoxelValue) {
      continue
    }

    if (!found[curZ]) {
      found[curZ] = []
    }
    found[curZ].push([curX, curY])

    // update boundaries
    if (curX > dfsBounds.bottomRight.x) {
      dfsBounds.bottomRight.x = curX
    }
    if (curX < dfsBounds.topLeft.x) {
      dfsBounds.topLeft.x = curX
    }
    if (curY > dfsBounds.bottomRight.y) {
      dfsBounds.bottomRight.y = curY
    }
    if (curY < dfsBounds.topLeft.y) {
      dfsBounds.topLeft.y = curY
    }
    if (curZ > dfsZRange.maxZ) {
      dfsZRange.maxZ = curZ
    }
    if (curZ < dfsZRange.minZ) {
      dfsZRange.minZ = curZ
    }

    if (stack.length > MAX_STACK_SIZE) {
      // Just break the execution if the stack gets too big
      error = DSFError.STACK_SIZE_EXCEEDED
      break
    }

    // Push neighbors to the stack
    if (!visitedVoxels[`${curX + 1},${curY},${curZ}`]) {
      stack.push([curX + 1, curY, curZ])
    }
    if (!visitedVoxels[`${curX - 1},${curY},${curZ}`]) {
      stack.push([curX - 1, curY, curZ])
    }
    if (!visitedVoxels[`${curX},${curY + 1},${curZ}`]) {
      stack.push([curX, curY + 1, curZ])
    }
    if (!visitedVoxels[`${curX},${curY - 1},${curZ}`]) {
      stack.push([curX, curY - 1, curZ])
    }
    if (!navigateZ) {
      continue
    }
    const nextZ = Math.min(totalFrames, curZ + 1)
    if (!visitedVoxels[`${curX},${curY},${nextZ}`]) {
      stack.push([curX, curY, nextZ])
    }
    const prevZ = Math.max(0, curZ - 1)
    if (!visitedVoxels[`${curX},${curY},${prevZ}`]) {
      stack.push([curX, curY, prevZ])
    }
  }

  return {
    found: found,
    bounds: dfsBounds,
    depth: dfsZRange,
    error: error,
  }
}
